import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export function MfaChallenge({ mfaToken, onSuccess }) {
  const { completeMfaChallenge } = useAuth();
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await completeMfaChallenge(mfaToken, code.trim(), useBackup);
      onSuccess(user);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(err?.response?.data?.detail
        || (status === 401 ? "That code is incorrect." : "Something went wrong. Please try again."));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mfa-challenge-form">
      <div className="flex items-start gap-2 rounded-md border border-border bg-secondary/60 p-3 text-sm text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>Enter the {useBackup ? "backup code" : "6-digit code from your authenticator app"} to finish signing in.</span>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="mfa-code">{useBackup ? "Backup code" : "Authentication code"}</Label>
        <Input
          id="mfa-code"
          autoFocus
          inputMode={useBackup ? "text" : "numeric"}
          maxLength={useBackup ? 24 : 6}
          value={code}
          onChange={(e) => setCode(useBackup ? e.target.value : e.target.value.replace(/\D/g, ""))}
          placeholder={useBackup ? "xxxx-xxxx-xxxx-xxxx-xxxx" : "123456"}
          data-testid="mfa-challenge-code-input"
        />
      </div>
      <Button type="submit" className="w-full" disabled={loading || code.length < 6} data-testid="mfa-challenge-submit">
        {loading ? "Verifying…" : "Verify"}
      </Button>
      <button
        type="button"
        className="w-full text-center text-xs text-primary hover:underline"
        onClick={() => { setUseBackup((v) => !v); setCode(""); }}
        data-testid="mfa-challenge-toggle-backup"
      >
        {useBackup ? "Use your authenticator app instead" : "Can't access your app? Use a backup code"}
      </button>
    </form>
  );
}
